import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowUpRight, AlertTriangle, HelpCircle } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { ContactForm } from "../components/ContactForm";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { FaqSection } from "../sections/FaqSection";
import { useUI } from "../context/UIContext";
import { getBranche, BRANCHEN_LIST } from "../data/branchen";
import { getBrancheDetail } from "../data/branchenDetail";
import { IMAGES } from "../data/content";

export default function BranchePage() {
  const { branche: slug } = useParams();
  const b = getBranche(slug);
  const detail = getBrancheDetail(slug);
  const { openUpload } = useUI();
  if (!b) return <Navigate to="/branchen" replace />;

  const title = `Energieberatung für ${b.name}`;
  const path = `/branche/${b.slug}`;
  const desc = `Gewerbestrom & Gas für ${b.name}: unabhängige Analyse, typische Einsparpotenziale und persönlicher Ansprechpartner. Kostenlos & unverbindlich.`;
  const schema = [{
    "@context": "https://schema.org", "@type": "Service",
    serviceType: `Energieberatung ${b.name}`,
    provider: { "@type": "ProfessionalService", name: "Energie-Tarifberater", areaServed: "DE" },
  }];
  if (detail?.faqs?.length) {
    schema.push({
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: detail.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    });
  }

  return (
    <>
      <Seo title={title} description={desc} path={path} image={IMAGES[b.img]} schema={schema} />
      <PageHero eyebrow={`Branche · ${b.lead}`} titleLines={["Energie für", `<span class="font-serif italic text-blue-700">${b.name}</span>`]}
        subtitle={desc} image={IMAGES[b.img]}
        breadcrumb={[{ label: "Gewerbekunden", to: "/gewerbekunden" }, { label: "Branchen", to: "/branchen" }, { label: b.name }]} />

      <section className="bg-background py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-2xl sm:text-3xl font-heading font-semibold text-navy-900 leading-snug mb-10">{detail?.longIntro || b.intro("")}</p>
            </Reveal>

            <Reveal>
              <h2 className="text-xl font-heading text-navy-900 mb-4">Typische Energieprobleme in dieser Branche</h2>
              <div className="space-y-3 mb-12">
                {b.painpoints.map((p, i) => (
                  <div key={i} className="flex gap-3 rounded-2xl bg-blue-50/60 border border-blue-100 p-5" data-testid={`branche-pain-${i}`}>
                    <AlertTriangle className="h-5 w-5 text-blue-700 shrink-0 mt-0.5" />
                    <p className="text-navy-900 text-sm leading-relaxed">{p}</p>
                  </div>
                ))}
              </div>
            </Reveal>

            {detail?.sections?.length > 0 && (
              <Reveal>
                <div className="space-y-8 mb-12" data-testid="branche-sections">
                  {detail.sections.map((s, i) => (
                    <div key={i} data-testid={`branche-section-${i}`}>
                      <h2 className="text-xl font-heading text-navy-900 mb-3">{s.h}</h2>
                      <p className="text-slate-600 leading-relaxed">{s.p}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
            )}

            <Reveal>
              <h2 className="text-xl font-heading text-navy-900 mb-4">Wie wir für {b.name} vorgehen</h2>
              <div className="grid sm:grid-cols-2 gap-6 mb-12">
                {b.benefits.map((be, i) => (
                  <div key={i} className="flex gap-4 rounded-2xl border border-slate-200 bg-white p-6 h-full" data-testid={`branche-benefit-${i}`}>
                    <span className="shrink-0 inline-grid place-items-center h-10 w-10 rounded-xl bg-blue-50 text-blue-700"><Check className="h-5 w-5" strokeWidth={2.5} /></span>
                    <p className="text-navy-900 text-sm leading-relaxed pt-1.5">{be}</p>
                  </div>
                ))}
              </div>
            </Reveal>

            {detail?.faqs?.length > 0 && (
              <Reveal>
                <h2 className="text-xl font-heading text-navy-900 mb-4 inline-flex items-center gap-2"><HelpCircle className="h-5 w-5 text-blue-700" /> Häufige Fragen</h2>
                <div className="space-y-4 mb-12" data-testid="branche-faqs">
                  {detail.faqs.map((f, i) => (
                    <div key={i} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`branche-faq-${i}`}>
                      <p className="font-heading text-navy-900 mb-2">{f.q}</p>
                      <p className="text-slate-500 text-sm leading-relaxed">{f.a}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
            )}

            <Reveal>
              <div className="rounded-3xl bg-white border border-slate-200 p-8 mb-6">
                <h2 className="text-xl font-heading text-navy-900 mb-3">Benötigte Unterlagen</h2>
                <p className="text-slate-500 text-sm leading-relaxed">Für eine erste Einschätzung genügt Ihre letzte Jahresabrechnung (Strom und/oder Gas). Für Gewerbe mit RLM zusätzlich die MaLo-ID bzw. das Lastprofil – beides steht auf Ihrer Rechnung.</p>
              </div>
              <h2 className="text-xl font-heading text-navy-900 mb-4">Weitere Branchen</h2>
              <div className="flex flex-wrap gap-x-5 gap-y-2">
                {BRANCHEN_LIST.filter((x) => x.slug !== b.slug).map((x) => (
                  <Link key={x.slug} to={`/branche/${x.slug}`} className="text-sm text-slate-500 hover:text-blue-700 transition-colors" data-testid={`branche-link-${x.slug}`}>{x.name}</Link>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
              className="lg:sticky lg:top-28 rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_40px_80px_-40px_rgba(0,0,0,0.15)]">
              <p className="text-xs uppercase tracking-[0.2em] text-blue-700 mb-2">Unverbindliches Angebot</p>
              <h3 className="text-2xl font-heading font-semibold text-navy-900 mb-6">Für Ihr <span className="font-serif italic text-blue-700">{b.nameSingular}</span></h3>
              <button onClick={openUpload} className="w-full group rounded-full bg-blue-700 hover:bg-blue-600 text-white py-4 font-medium transition-colors flex items-center justify-center gap-2 mb-5" data-testid="branche-upload-btn">Rechnung hochladen <ArrowUpRight className="h-4 w-4" /></button>
              <ContactForm compact topic={`branche-${b.slug}`} testid={`branche-form-${b.slug}`} />
            </motion.div>
          </div>
        </div>
      </section>

      <FaqSection />
    </>
  );
}
